import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { LogApi } from './api/log-api.service';
import { LogMessage } from '../models/log-message';

const DEFAULT_LOAD_TIMEOUT_MS = 5000;
const LOG_PREFIX = '[Carey2 remote chatbot]';
const CHAT_ERROR_PATTERN =
  /chatbot|carey2|chtaws|cdn\.botframework\.com|directline|LogJsErrors/i;

export interface WebchatApi {
  config(config: unknown): void;
  initChat(payload: unknown, claimData: unknown): void;
  endConversation(): Promise<void> | void;
  isConversationOn?(): boolean;
}

declare const webchat: WebchatApi | undefined;

@Injectable({
  providedIn: 'root',
})
export class ChatBotScriptLoaderService {
  constructor(
    private readonly http: HttpClient,
    private readonly logApi: LogApi,
  ) {}

  private rejectionHandler: ((e: PromiseRejectionEvent) => void) | null = null;
  private chatReadyPromise: Promise<boolean> | null = null;

  ensureChatReady(): Promise<boolean> {
    if (!this.chatReadyPromise) {
      this.chatReadyPromise = this.loadScript().then((ready) => {
        if (!ready) {
          this.chatReadyPromise = null;
        }
        return ready;
      });
    }

    return this.chatReadyPromise;
  }

  getWebchat(): WebchatApi | undefined {
    return typeof webchat !== 'undefined' ? webchat : undefined;
  }

  private confirmWebchatReady(): boolean {
    if (!this.getWebchat()) {
      this.logWarning(
        `${LOG_PREFIX} webchat was not defined after the script executed.`,
      );
      return false;
    }

    this.registerChatErrorListener();
    return true;
  }

  private loadScript(): Promise<boolean> {
    return (async () => {
      let url: string;
      try {
        url = await this.getRemoteCarey2ChatbotUrl();
      } catch (err) {
        this.logWarning(
          `${LOG_PREFIX} Could not resolve remote script URL from API: ${err}`,
        );
        return false;
      }

      const trimmed = (url ?? '').trim();
      if (!trimmed) {
        this.logWarning(
          `${LOG_PREFIX} Remote script URL was empty; skipping load.`,
        );
        return false;
      }

      // Re-injecting is not harmless: a second execution of the same classic
      // script throws on the duplicate `const webchat` declaration.
      if (this.isCarey2ScriptTagPresent(trimmed)) {
        return this.confirmWebchatReady();
      }

      return await new Promise<boolean>((resolve) => {
        let settled = false;
        const finish = (loaded: boolean): void => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(loadTimeout);
          resolve(loaded);
        };
        const loadTimeout = setTimeout(() => {
          this.logWarning(
            `${LOG_PREFIX} Script load timed out after ${DEFAULT_LOAD_TIMEOUT_MS}ms: ${trimmed}`,
          );
          finish(false);
        }, DEFAULT_LOAD_TIMEOUT_MS);

        try {
          const script = document.createElement('script');
          script.async = true;
          script.type = 'text/javascript';
          script.charset = 'utf-8';
          script.src = trimmed;
          script.onload = () => {
            finish(this.confirmWebchatReady());
          };
          script.onerror = () => {
            this.logWarning(
              `${LOG_PREFIX} Script element failed to load (network or blocked): ${trimmed}`,
            );
            finish(false);
          };
          document.head.appendChild(script);
        } catch (error) {
          this.logWarning(
            `${LOG_PREFIX} Failed to inject script tag: ${error}`,
          );
          finish(false);
        }
      });
    })();
  }

  private isCarey2ScriptTagPresent(url: string): boolean {
    return Array.from(document.getElementsByTagName('script')).some((s) => {
      const attr = s.getAttribute('src');
      if (!attr) {
        return false;
      }
      return attr === url || s.src === url || s.src.endsWith(url);
    });
  }

  private getRemoteCarey2ChatbotUrl(): Promise<string> {
    return firstValueFrom(
      this.http.get('api/user/RemoteCarey2ChatbotUrl', {
        responseType: 'text',
      }),
    );
  }

  private registerChatErrorListener(): void {
    if (this.rejectionHandler) {
      return;
    }
    this.rejectionHandler = (event: PromiseRejectionEvent) => {
      const msg = String(event.reason?.message ?? event.reason ?? '');
      const stack = String(event.reason?.stack ?? '');
      if (!CHAT_ERROR_PATTERN.test(msg + stack)) {
        return;
      }
      event.preventDefault();
      const logMessage = new LogMessage();
      logMessage.message = `${LOG_PREFIX} Suppressed chat rejection: ${msg}`;
      this.logApi.log(logMessage, 'Error');
    };
    window.addEventListener('unhandledrejection', this.rejectionHandler);
  }

  private logWarning(message: string): void {
    const logMessage = new LogMessage();
    logMessage.message = message;
    this.logApi.log(logMessage, 'Warning');
  }
}
